import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, mediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Reveal } from '@/components/public/anim';
import { Seo } from '@/components/Seo';
import { fmtDate } from '@/pages/public/Blog';
import { ArrowLeft, Calendar, Eye, Share2, MessageCircle, Send, Loader2, User, CalendarDays, MapPin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const FALLBACK_IMG = 'https://images.pexels.com/photos/33343202/pexels-photo-33343202.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600';

// Converte un link YouTube/Vimeo (o un mp4 diretto) in un embed sicuro (privacy-enhanced YT).
function parseVideo(url) {
  const u = (url || '').trim();
  if (!u) return null;
  let m = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i);
  if (m) return { type: 'iframe', src: `https://www.youtube-nocookie.com/embed/${m[1]}` };
  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (m) return { type: 'iframe', src: `https://player.vimeo.com/video/${m[1]}` };
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(u)) return { type: 'file', src: u };
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(undefined);
  const [related, setRelated] = useState([]);
  const [comments, setComments] = useState([]);
  const [form, setForm] = useState({ author_name: '', email: '', content: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let active = true;
    setPost(undefined);
    api.get(`/blog/public/posts/${slug}`).then(({ data }) => {
      if (!active) return;
      setPost(data);
      api.post(`/blog/public/posts/${slug}/track`, { type: 'view' }).catch(() => {});
      api.get('/blog/public/posts').then(({ data: all }) => {
        if (!active) return;
        setRelated((all || []).filter((p) => p.slug !== slug).slice(0, 3));
      }).catch(() => {});
      api.get(`/blog/public/posts/${slug}/comments`).then(({ data: cs }) => active && setComments(cs || [])).catch(() => {});
    }).catch(() => active && setPost(null));
    return () => { active = false; };
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: post?.title, url });
      else { await navigator.clipboard.writeText(url); toast.success('Link copiato!'); }
    } catch (e) { /* cancelled */ }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post('/blog/public/comments', { slug, ...form });
      toast.success(t('pages.comments.sent'));
      if (data?.status === 'approved' && data.comment) setComments((c) => [...c, data.comment]);
      setForm({ author_name: '', email: '', content: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio commento');
    } finally { setSending(false); }
  };

  return (
    <PublicLayout>
      {post && (
        <Seo
          title={post.seo?.title || post.title}
          description={post.seo?.description || post.excerpt}
          image={post.cover_image}
          path={`/blog/${slug}`}
          type="article"
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.excerpt,
            image: post.cover_image ? [post.cover_image] : undefined,
            datePublished: post.published_at || post.created_at,
            dateModified: post.updated_at || post.published_at || post.created_at,
            author: { '@type': 'Person', name: post.author?.name || 'Redazione Sila' },
            publisher: { '@type': 'Organization', name: 'Sila Experiences' },
            articleSection: post.category?.name,
          }}
        />
      )}
      {post === undefined && (
        <div className="sila-section pt-28"><Skeleton className="h-8 w-40" /><Skeleton className="mt-6 h-14 w-3/4" /><Skeleton className="mt-8 h-[380px] w-full rounded-[28px]" /></div>
      )}

      {post === null && (
        <div className="sila-section flex min-h-[60vh] flex-col items-center justify-center text-center" data-testid="blogpost-notfound">
          <h1 className="font-display text-3xl font-bold">404</h1>
          <p className="mt-2 text-muted-foreground">Articolo non trovato.</p>
          <Button asChild className="mt-6 rounded-full"><Link to="/blog"><ArrowLeft className="mr-2 h-4 w-4" />{t('pages.blog.back')}</Link></Button>
        </div>
      )}

      {post && (
        <>
          <article className="sila-section max-w-3xl pt-28">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="blogpost-back">
              <ArrowLeft className="h-4 w-4" />{t('pages.blog.back')}
            </Link>
            {post.category?.name && <Badge variant="secondary" className="ml-0 mt-6 block w-fit rounded-full">{post.category.name}</Badge>}
            <h1 className="mt-4 font-display text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl" data-testid="blogpost-title">{post.title}</h1>

            <div className="mt-6 flex flex-wrap items-center gap-4 border-b border-border pb-6">
              <div className="flex items-center gap-2.5">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary/12 text-primary"><User className="h-4 w-4" /></AvatarFallback></Avatar>
                <div className="text-sm">
                  <div className="font-medium">{post.author?.name || 'Redazione Sila'}</div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{fmtDate(post.published_at || post.created_at, i18n.language)}</span>
                    <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{post.stats?.views || 0} {t('pages.blog.views')}</span>
                  </div>
                </div>
              </div>
              <Button onClick={share} variant="outline" size="sm" className="ml-auto rounded-full" data-testid="blogpost-share"><Share2 className="mr-1.5 h-4 w-4" />{t('pages.blog.share')}</Button>
            </div>
          </article>

          <div className="sila-section max-w-4xl">
            <div className="mt-8 overflow-hidden rounded-[28px] shadow-[var(--shadow-soft)]">
              <img loading="lazy" src={post.cover_image || FALLBACK_IMG} alt={post.title} className="h-auto w-full object-cover" />
            </div>

            {/* Video (YouTube/Vimeo/MP4) — #14 */}
            {(() => {
              const v = parseVideo(post.video_url);
              if (!v) return null;
              return (
                <div className="mt-6 overflow-hidden rounded-[28px] bg-black shadow-[var(--shadow-soft)]" data-testid="blogpost-video">
                  <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
                    {v.type === 'iframe' ? (
                      <iframe
                        src={v.src}
                        title={post.title}
                        className="absolute inset-0 h-full w-full"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video src={v.src} controls preload="metadata" className="absolute inset-0 h-full w-full" />
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          <article className="sila-section max-w-3xl py-12">
            {post.excerpt && <p className="mb-8 border-l-4 border-primary pl-4 text-xl font-medium leading-relaxed text-foreground/90">{post.excerpt}</p>}
            <div className="sila-prose" data-testid="blogpost-content" dangerouslySetInnerHTML={{ __html: post.content || '' }} />
          </article>

          {/* Banner evento promozionale — #14 */}
          {post.event_banner && (
            <section className="sila-section max-w-3xl pb-4">
              <Link
                to="/eventi"
                className="group grid grid-cols-1 overflow-hidden rounded-[24px] border border-border bg-card shadow-[var(--shadow-sm)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)] sm:grid-cols-[220px_1fr]"
                data-testid="blogpost-event-banner"
              >
                <div className="relative h-40 overflow-hidden bg-muted sm:h-full">
                  <img
                    loading="lazy"
                    src={post.event_banner.cover_image ? mediaUrl(post.event_banner.cover_image) : FALLBACK_IMG}
                    alt={post.event_banner.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-sun)/0.92)] px-3 py-1 text-xs font-semibold text-[hsl(var(--brand-bark))]">
                    <CalendarDays className="h-3.5 w-3.5" />Evento
                  </span>
                </div>
                <div className="flex flex-col justify-center p-6">
                  {(post.event_banner.start_date) && (
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                      {post.event_banner.start_date}{post.event_banner.end_date ? ` — ${post.event_banner.end_date}` : ''}
                    </p>
                  )}
                  <h3 className="mt-1 font-display text-xl font-bold leading-snug">{post.event_banner.title}</h3>
                  {post.event_banner.location && (
                    <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{post.event_banner.location}</p>
                  )}
                  {post.event_banner.description && (
                    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{post.event_banner.description}</p>
                  )}
                  <span className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-primary transition-transform group-hover:translate-x-1">
                    Scopri l'evento <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </section>
          )}

          {/* Comments */}
          <section className="sila-section max-w-3xl pb-16">
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold"><MessageCircle className="h-5 w-5 text-primary" />{t('pages.comments.title')} ({comments.length})</h2>
            <div className="mt-6 space-y-4">
              {comments.length === 0 && <p className="text-sm text-muted-foreground" data-testid="comments-empty">{t('pages.comments.empty')}</p>}
              {comments.map((c) => (
                <div key={c.id} className="rounded-2xl border border-border bg-card p-4" data-testid="comment-item">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8"><AvatarFallback className="bg-secondary text-xs">{(c.author_name || 'A').slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                    <span className="text-sm font-medium">{c.author_name}</span>
                    <span className="text-xs text-muted-foreground">{fmtDate(c.created_at, i18n.language)}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.content}</p>
                </div>
              ))}
            </div>

            <form onSubmit={submitComment} className="mt-8 space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="comment-form">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input required placeholder={t('pages.comments.name')} value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} data-testid="comment-name" />
                <Input required type="email" placeholder={t('pages.comments.email')} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="comment-email" />
              </div>
              <Textarea required rows={3} placeholder={t('pages.comments.message')} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="comment-message" />
              <Button type="submit" disabled={sending} className="rounded-full" data-testid="comment-submit">{sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}{t('pages.comments.submit')}</Button>
            </form>
          </section>

          {/* Related */}
          {related.length > 0 && (
            <section className="sila-section pb-24">
              <Reveal><h2 className="font-display text-2xl font-bold">{t('pages.blog.related')}</h2></Reveal>
              <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-3">
                {related.map((p) => (
                  <Link key={p.id} to={`/blog/${p.slug}`} className="group block" data-testid={`related-${p.slug}`}>
                    <div className="relative h-44 overflow-hidden rounded-[20px] shadow-[var(--shadow-sm)]">
                      <img loading="lazy" src={p.cover_image || FALLBACK_IMG} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <h3 className="mt-3 font-display text-lg font-semibold leading-snug transition-colors group-hover:text-primary">{p.title}</h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </PublicLayout>
  );
}
